/**
 * Pipeline Dates Composable
 * UX/UI Sprint 2 - Task 2.1
 *
 * Provides relative date formatting with contextual styling
 * Supports: created_at, deadline, due_date
 */

import { computed } from 'vue';
import {
  formatDistanceToNow,
  differenceInCalendarDays,
  parseISO,
} from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useStore } from 'vuex';
import { formatInReportingTimezone } from 'dashboard/routes/dashboard/pipeline/utils/localeFormatting';

// Locale mapping
const locales = {
  'pt-BR': ptBR,
  pt_BR: ptBR,
  pt: ptBR,
  en: enUS,
  'en-US': enUS,
};

/**
 * Get the appropriate locale for date-fns.
 *
 * Card-4.6 (2026-04-29) — antes lia chatwootConfig.selectedLocale, que
 * é o default GLOBAL do app (env var), não o locale do usuário/conta.
 * Resultado: usuário em pt_BR via "Today", "In 12 days" em vez de
 * "Hoje", "Em 12 dias".
 *
 * Fix: ler do i18n instance global (exposto em window.__APP_I18N__
 * pelo entrypoint dashboard.js). Reativo: setLocale() em App.vue muda
 * `i18n.global.locale.value` e nossa função pega o valor atual a cada
 * chamada.
 */
const getLocale = () => {
  const userLocale =
    // eslint-disable-next-line no-underscore-dangle
    window.__APP_I18N__?.global?.locale?.value ||
    window.chatwootConfig?.selectedLocale ||
    document.documentElement.lang ||
    'pt-BR';
  return locales[userLocale] || ptBR;
};

/**
 * Parse a date string or Date object
 */
const parseDate = date => {
  if (!date) return null;
  if (date instanceof Date) return date;
  try {
    return parseISO(date);
  } catch {
    return new Date(date);
  }
};

/**
 * Check whether the input represents an instant rather than a calendar date.
 * Explicit midnight timestamps still contain time information and must be
 * rendered in the account timezone, where they may no longer be midnight.
 * @param {Date|string} dateInput - The original date input
 * @returns {boolean}
 */
const hasTimeInfo = dateInput =>
  dateInput instanceof Date ||
  (typeof dateInput === 'string' && /T\d{2}:\d{2}/.test(dateInput.trim()));

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const safeTimeZone = timeZone => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format();
    return timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Format a relative date with contextual information
 * @param {Date|string} date - The date to format
 * @param {Object} options - Formatting options
 * @returns {Object} { text, status, color, time, fullDateTime }
 */
export const formatRelativeDate = (date, options = {}) => {
  const isDateOnly =
    typeof date === 'string' && DATE_ONLY_PATTERN.test(date.trim());
  // A date-only value is a calendar date, not an instant. Parsing it in the
  // browser timezone and then converting it to the account timezone can move
  // it one day backward. Noon UTC is used only as a stable display anchor;
  // comparisons keep the original YYYY-MM-DD key.
  const parsedDate = isDateOnly
    ? new Date(`${date.trim()}T12:00:00.000Z`)
    : parseDate(date);
  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return {
      text: null,
      status: 'invalid',
      color: null,
      time: null,
      fullDateTime: null,
    };
  }

  const locale = getLocale();
  const now = new Date();
  const timeZone = safeTimeZone(options.timeZone || 'UTC');
  const dateKey = isDateOnly
    ? date.trim()
    : formatInReportingTimezone(parsedDate, 'yyyy-MM-dd', timeZone);
  const todayKey = formatInReportingTimezone(now, 'yyyy-MM-dd', timeZone);
  const daysDiff = differenceInCalendarDays(
    parseISO(dateKey),
    parseISO(todayKey)
  );
  const isTodayInTimezone = daysDiff === 0;
  const isTomorrowInTimezone = daysDiff === 1;
  const isYesterdayInTimezone = daysDiff === -1;
  const isOverdue = isDateOnly ? daysDiff < 0 : parsedDate < now;

  // Check if the original date string has time information
  const includesTime = hasTimeInfo(date);
  const time = includesTime
    ? formatInReportingTimezone(parsedDate, 'HH:mm', timeZone)
    : null;

  // Determine status and color
  let status = 'normal';
  let color = 'slate';

  if (options.isDeadline) {
    if (isOverdue) {
      status = 'overdue';
      color = 'red';
    } else if (isTodayInTimezone) {
      status = 'due-today';
      color = 'orange';
    } else if (isTomorrowInTimezone) {
      status = 'due-tomorrow';
      color = 'yellow';
    } else if (daysDiff <= 3 && daysDiff > 0) {
      status = 'due-soon';
      color = 'blue';
    }
  }

  // Format text based on proximity
  let text;

  if (isTodayInTimezone) {
    text = locale === ptBR ? 'Hoje' : 'Today';
  } else if (isTomorrowInTimezone) {
    text = locale === ptBR ? 'Amanhã' : 'Tomorrow';
  } else if (isYesterdayInTimezone) {
    text = locale === ptBR ? 'Ontem' : 'Yesterday';
  } else if (isOverdue && options.isDeadline) {
    const absDays = Math.abs(daysDiff);
    if (absDays === 1) {
      text = locale === ptBR ? 'Atrasado 1 dia' : 'Overdue 1 day';
    } else {
      text =
        locale === ptBR
          ? `Atrasado ${absDays} dias`
          : `Overdue ${absDays} days`;
    }
  } else if (daysDiff > 0 && daysDiff <= 7) {
    text = locale === ptBR ? `Em ${daysDiff} dias` : `In ${daysDiff} days`;
  } else if (daysDiff < 0 && daysDiff >= -7) {
    const absDays = Math.abs(daysDiff);
    text = locale === ptBR ? `Há ${absDays} dias` : `${absDays} days ago`;
  } else {
    // Use date-fns for longer distances, capitalize first letter for consistency
    const raw = formatDistanceToNow(parsedDate, {
      locale,
      addSuffix: true,
    });
    text = raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  // Include time in text if available.
  // `includeTime: false` devolve só a parte relativa — usado pelo card do
  // kanban, onde a hora de criação é ruído. `time` continua exposto para quem
  // precisa da hora separada.
  const includeTime = options.includeTime !== false;
  const textWithTime = time && includeTime ? `${text} ${time}` : text;

  // Format full date time for tooltip
  const fullDateTime = new Intl.DateTimeFormat(
    locale === ptBR ? 'pt-BR' : 'en-US',
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: time ? '2-digit' : undefined,
      minute: time ? '2-digit' : undefined,
      timeZone: isDateOnly ? 'UTC' : timeZone,
    }
  ).format(parsedDate);

  return { text: textWithTime, status, color, time, fullDateTime };
};

/**
 * Composable for pipeline card dates
 * @param {Ref} card - Reactive card object
 */
export const usePipelineDates = card => {
  const store = useStore();
  const currentAccount = computed(
    () =>
      store.getters['accounts/getAccount']?.(
        store.getters.getCurrentAccountId
      ) || store.getters.getCurrentAccount
  );
  const reportingTimezone = computed(
    () =>
      currentAccount.value?.reporting_timezone ||
      currentAccount.value?.settings?.reporting_timezone ||
      'UTC'
  );

  // Card creation time (lifetime)
  const cardLifetime = computed(() => {
    const createdAt = card.value?.created_at;
    if (!createdAt) return null;

    const result = formatRelativeDate(createdAt, {
      isDeadline: false,
      timeZone: reportingTimezone.value,
    });
    return result.text;
  });

  // Card lifetime without the time-of-day suffix.
  // The kanban card shows "how long this card has existed" as context, where
  // the creation hour is noise ("Há 2 meses 21:16" reads as two units without
  // a label). Detail views keep the full `cardLifetime`.
  const cardLifetimeShort = computed(() => {
    const createdAt = card.value?.created_at;
    if (!createdAt) return null;

    return formatRelativeDate(createdAt, {
      isDeadline: false,
      includeTime: false,
      timeZone: reportingTimezone.value,
    }).text;
  });

  // Tempo decorrido na etapa atual.
  // Vive aqui (e não no componente) porque este composable já resolve o locale
  // do date-fns; o badge de SLA do kanban precisa dos dois formatos: `seconds`
  // para comparar com o limite da etapa e `text` para a frase localizada
  // ("cerca de 5 horas" / "about 5 hours"). Timezone é irrelevante: duração
  // decorrida não muda de fuso.
  const timeInCurrentStage = computed(() => {
    // Fallback para created_at igual ao de PipelineCard#time_in_current_stage:
    // card legado/importado sem `stage_entered_at` conta desde a criação. Sem
    // isso o badge de SLA discordaria do backend justamente nos cards antigos.
    const enteredAt = card.value?.stage_entered_at || card.value?.created_at;
    if (!enteredAt) return null;

    const parsed = parseDate(enteredAt);
    if (!parsed || Number.isNaN(parsed.getTime())) return null;

    return {
      seconds: Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 1000)),
      text: formatDistanceToNow(parsed, { locale: getLocale() }),
    };
  });

  // Deadline/due date with status
  const deadline = computed(() => {
    // Prefer deadline_at (with time support) over deadline (date only)
    const dueDate =
      card.value?.item_details?.deadline_at ||
      card.value?.item_details?.deadline ||
      card.value?.item_details?.due_date ||
      card.value?.deadline;

    if (!dueDate) return null;

    return formatRelativeDate(dueDate, {
      isDeadline: true,
      timeZone: reportingTimezone.value,
    });
  });

  // Scheduled date for activities/follow-ups
  const scheduledAt = computed(() => {
    const scheduled =
      card.value?.scheduled_at || card.value?.item_details?.scheduled_at;

    if (!scheduled) return null;

    return formatRelativeDate(scheduled, {
      isDeadline: false,
      timeZone: reportingTimezone.value,
    });
  });

  // Has scheduled date
  const hasScheduledDate = computed(() => {
    return scheduledAt.value?.text != null;
  });

  // CSS classes for deadline styling
  const deadlineClasses = computed(() => {
    if (!deadline.value) return '';

    const colorMap = {
      red: 'text-red-600 dark:text-red-400',
      orange: 'text-orange-600 dark:text-orange-400',
      yellow: 'text-yellow-600 dark:text-yellow-400',
      blue: 'text-blue-600 dark:text-blue-400',
      slate: 'text-slate-500 dark:text-slate-400',
    };

    return colorMap[deadline.value.color] || colorMap.slate;
  });

  // Background classes for deadline badge
  const deadlineBadgeClasses = computed(() => {
    if (!deadline.value) return '';

    const colorMap = {
      red: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
      orange:
        'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300',
      yellow:
        'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
      blue: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
      slate:
        'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300',
    };

    return colorMap[deadline.value.color] || colorMap.slate;
  });

  // Is the card overdue?
  const isOverdue = computed(() => {
    return deadline.value?.status === 'overdue';
  });

  // Is the card due soon (within 3 days)?
  const isDueSoon = computed(() => {
    const status = deadline.value?.status;
    return (
      status === 'due-today' ||
      status === 'due-tomorrow' ||
      status === 'due-soon'
    );
  });

  return {
    cardLifetime,
    cardLifetimeShort,
    deadline,
    deadlineClasses,
    deadlineBadgeClasses,
    isOverdue,
    isDueSoon,
    scheduledAt,
    hasScheduledDate,
    timeInCurrentStage,
  };
};

export default usePipelineDates;
